import logger, { useStoreLogger } from '../logger.ts'
import {
  CartCreate,
  CartItemCreate,
  CartItemDelete,
  CartItemUpdate,
  CartShippingAddressCreate,
  CartBillingAddressCreate,
  CartBatchedRequests,
  CartGet,
  CartPaymentDelete,
  CartCustomerAdd,
  CartGuestAdd,
  CartEventAdd,
  CartEventDelete,
  CartPayeeAdd,
  CartShipToTypeCreate,
  CartShippingMethodCreate,
  CartFreeDollarCouponAdd,
  CartFreeDollarCouponRemove,
  CartActiveUpdate,
  CartConsultantLeadCreate,
  CartConsultantLeadDelete,
  CartPartyLeadCreate,
  CartPartyLeadDelete,
  CartInvoiceCreate,
  CartInvoiceUpdate,
  InvoiceGet,
  InvoiceGetList,
  InvoiceSubmit,
  InvoiceExists as InvoiceExistsApi,
  InvoiceVerifyIdentity as InvoiceVerifyIdentityApi,
  CartCouponCreate,
  CartInvoiceCancel,
  CartInvoiceResend,
  CartGetByCustomer
} from '../api/Cart'
import type {
  ICartResponse,
  IAoeCart,
  ICartBatchedRequests,
  ICartItemCreateCommon,
  ICartItemCreate,
  ICurrentCartIdentifiers,
  ICartItemDelete,
  ICartItemUpdate,
  ICartCustomerAdd,
  ICartGuestAdd,
  ICartEventAdd,
  ICartEventDelete,
  ICartShippingAddressCreate,
  ICartPaymentDelete,
  ICartPayeeAdd,
  ICartShipToTypeCreate,
  ICartShippingMethodCreate,
  ICartFreeDollarCouponAdd,
  ICartActiveUpdate,
  ICartTokenRequest,
  ICartPartyLead,
  IAoeInvoiceRequest,
  IAoeInvoiceSubmitRequest,
  IAoeInvoiceVerifyIdentityRequest,
  IAoeInvoiceSubmitResponse,
  IAoeInvoiceResponse,
  IAoeInvoiceListResponse,
  ICartCouponCreate,
  ICartGetByCustomerRequest,
  IAoeCartEarnedDiscount,
  IAoeCartGamification,
} from '../types/CartTypes.ts'
import type { IAddressVerification } from '../types/CountryCultureTypes'
import { useCookies } from 'vue3-cookies'
import type { ComposerTranslation } from 'vue-i18n'
import { FormatCurrency } from '@/services/currencyFormatter'
import { GetKitOrderTypeId } from '../stores/SettingsStore'


// Order types whose carts must never be written to local storage. Local storage is keyed by
// customer only and is read by the shop cart page, so a consultant's personal order cart
// (AoeOrderEntry) written there was picked up by the shop in another tab, which then re-synced
// its payee to the site consultant (payee misattribution). AoeOrderEntry always loads its cart
// from the server, so it registers its order type here to keep that cart out of local storage.
const orderTypeIdsExcludedFromLocalStorage = new Set<string>()

export function ExcludeOrderTypeFromLocalStorage(orderTypeId: string) {
  if (orderTypeId) orderTypeIdsExcludedFromLocalStorage.add(orderTypeId)
}

export function SaveCartToLocalStorage(cartResponse: ICartResponse) {
  const newValue = JSON.stringify(cartResponse.item)
  const logger = useStoreLogger()
  logger.debug('SaveCartToLocalStorage', newValue)

  const cartOrderTypeId = cartResponse.item?.orderTypeId
  if (cartOrderTypeId && orderTypeIdsExcludedFromLocalStorage.has(cartOrderTypeId)) {
    logger.debug('SaveCartToLocalStorage skipped - order type excluded from local storage', cartOrderTypeId)
    return
  }

  // Get the customer's beeNumber from the cart item
  let cartBeeNumber = cartResponse.item?.beeNumberCustomer || null

  // Get the authenticated user's beeNumber
  const authBeeNumber = GetBeeNumberFromCookies()

  // Use the cart's beeNumber if available, otherwise use the authenticated user's beeNumber
  cartBeeNumber = cartBeeNumber || authBeeNumber

  // Save to user-specific storage if beeNumber is available
  if (cartBeeNumber !== null) {
    const userKey = GetUserCartKey(cartBeeNumber)
    localStorage.setItem(userKey, newValue)
    logger.debug('Saved cart to user-specific storage with key:', userKey)

    // Dispatch event for user-specific key
    window.dispatchEvent(
      new StorageEvent('storage', { key: userKey, newValue, storageArea: localStorage }),
    )

    // cartResponse.hostCart (returned when a guest cart on an event changes) is deliberately not
    // cached: nothing reads it back, and writing another customer's cart into this browser's
    // storage lets the shop cart page restore it as the current cart in another tab.
  }

  // Always save to default storage for backward compatibility
  const defaultKey = CartLocalStorageKey
  localStorage.setItem(defaultKey, newValue)

  // Dispatch event for default key
  window.dispatchEvent(
    new StorageEvent('storage', { key: defaultKey, newValue, storageArea: localStorage }),
  )
}

export function ClearCartFromLocalStorage() {
  const logger = useStoreLogger()
  logger.debug('ClearCartFromLocalStorage')

  // Get the authenticated user's beeNumber
  const beeNumber = GetBeeNumberFromCookies()

  if (beeNumber !== null) {
    // Clear user-specific cart
    const userKey = GetUserCartKey(beeNumber)
    localStorage.removeItem(userKey)
    window.dispatchEvent(
      new StorageEvent('storage', {
        key: userKey,
        newValue: null,
        storageArea: localStorage,
      }),
    )
  }

  // Always clear default cart for backward compatibility
  const defaultKey = CartLocalStorageKey
  localStorage.removeItem(defaultKey)
  window.dispatchEvent(
    new StorageEvent('storage', {
      key: defaultKey,
      newValue: null,
      storageArea: localStorage,
    }),
  )
}

// Base key for cart storage
export const CartLocalStorageKey = 'AoeCart'

// Function to get user-specific cart storage key
export function GetUserCartKey(beeNumber: number | null): string {
  return beeNumber ? `${CartLocalStorageKey}_${beeNumber}` : CartLocalStorageKey
}

// Helper function to get the authenticated user's beeNumber from cookies
function GetBeeNumberFromCookies(): number | null {
  const { cookies } = useCookies()
  const sessionInfo = cookies.get('SessionInformation')
  let beeNumber: number | null = null

  if (sessionInfo) {
    try {
      // Try to extract beeNumber from the session info
      const decodedToken = JSON.parse(atob(sessionInfo.split('.')[1]))
      if (decodedToken.BeeNumberAuthenicatedUser) {
        beeNumber = Number(decodedToken.BeeNumberAuthenicatedUser)
      }
    } catch (e) {
      console.error('Error parsing session info:', e)
    }
  }

  return beeNumber
}

export function GetCartFromLocalStorage(
  aoeCartIdToken: string
): IAoeCart | null {
  const logger = useStoreLogger()
  logger.debug('GetCartFromLocalStorage', aoeCartIdToken)

  // Get the authenticated user's beeNumber
  const beeNumber = GetBeeNumberFromCookies()

  // Try to get the cart from the user-specific key first if beeNumber is available
  if (beeNumber !== null) {
    const userKey = GetUserCartKey(beeNumber)
    const userJson = localStorage.getItem(userKey)

    if (userJson && userJson !== "undefined") {
      logger.debug('Cart found in user-specific local storage')
      const userCart: IAoeCart = JSON.parse(userJson)
      if (userCart.aoeCartIdToken === aoeCartIdToken) {
        return userCart
      }
    }
  }

  // Fall back to the default key if not found in user-specific storage
  const defaultKey = CartLocalStorageKey
  const defaultJson = localStorage.getItem(defaultKey)

  if (defaultJson && defaultJson !== "undefined") {
    logger.debug('Cart found in default local storage')
    const defaultCart: IAoeCart = JSON.parse(defaultJson)
    if (defaultCart.aoeCartIdToken === aoeCartIdToken) {
      return defaultCart
    }
  }

  logger.debug('Cart not found in local storage')
  return null
}

export async function GetCartFromLocalStorageOrApi(
  aoeCartIdToken: string
): Promise<IAoeCart | null> {
  const logger = useStoreLogger()
  logger.debug('GetCartFromLocalStorageOrApi', aoeCartIdToken)

  // Try to get the cart from local storage first
  let cart = GetCartFromLocalStorage(aoeCartIdToken)
  if (cart) return cart

  // If not found in local storage, fetch from API
  const cartResponse = await CartGet(aoeCartIdToken)

  if (cartResponse) {
    // Save the cart to local storage
    SaveCartToLocalStorage(cartResponse)

    // Get the cart from local storage again
    cart = GetCartFromLocalStorage(aoeCartIdToken)
  }
  return cart
}

// Forces a fresh read from the server and re-caches it, instead of trusting whatever is
// already in local storage (GetCartFromLocalStorageOrApi's cache-first behavior is right for
// routine refreshes, but wrong right after a mutation failed - the server can now prune a line
// item on its own, e.g. a SKU that's gone unavailable, and the stale cached cart would otherwise
// keep showing that ghost item forever, causing every further action on it to fail the same way
// with a raw "not found" error). Callers should use this specifically to resync after a cart
// mutation call throws.
export async function RefreshCartFromApi(
  aoeCartIdToken: string
): Promise<IAoeCart | null> {
  const logger = useStoreLogger()
  logger.debug('RefreshCartFromApi', aoeCartIdToken)

  const cartResponse = await CartGet(aoeCartIdToken)
  if (cartResponse) {
    SaveCartToLocalStorage(cartResponse)
    return GetCartFromLocalStorage(aoeCartIdToken)
  }
  return null
}

export function GetCurrentCartFromLocalStorage(): IAoeCart | null {
  const logger = useStoreLogger()
  logger.debug('GetCurrentCartFromLocalStorage')

  // Get the current cart identifiers from cookies
  const cartIdentifiers = GetCurrentCartIdentifiers()

  // If we have a cart token in cookies, try to get the cart
  if (cartIdentifiers) {
    return GetCartFromLocalStorage(cartIdentifiers.aoeCartIdToken)
  }

  // If no cart token in cookies, try to restore the cart for the current user
  const beeNumber = GetBeeNumberFromCookies()
  if (beeNumber !== null) {
    // Look for a cart in user-specific storage
    const userKey = GetUserCartKey(beeNumber)
    const userJson = localStorage.getItem(userKey)

    if (userJson && userJson !== "undefined") {
      logger.debug('Found cart in user-specific storage for returning user')
      const userCart: IAoeCart = JSON.parse(userJson)

      // Create a new cart token cookie for this cart
      const { cookies } = useCookies()
      cookies.set('AoeCartIdToken', userCart.aoeCartIdToken)

      return userCart
    }
  }

  return null
}

export async function GetCartItemCount(
  aoeCartIdToken: string,
): Promise<number> {
  const cart = await GetCartFromLocalStorageOrApi(aoeCartIdToken)
  if (cart) {
    // Exclude kit carts — they use a different orderTypeId than the regular one
    const kitOrderTypeId = await GetKitOrderTypeId()
    if (cart.orderTypeId && cart.orderTypeId === kitOrderTypeId) {
      return 0
    }

    const itemCount = cart.items.reduce(function (prev, cur) {
      return prev + cur.quantity
    }, 0)
    return itemCount
  }
  return 0
}

export function GetUniqueProductCodes(cart: IAoeCart) {
  const logger = useStoreLogger()
  logger.debug('GetUniqueProductCodes', cart)
  const uniqueProductCodes: Array<string> = []
  for (const item of cart.items) {
    if (!uniqueProductCodes.includes(item.productCode)) {
      uniqueProductCodes.push(item.productCode)
    }
  }
  logger.debug('uniqueProductCodes=', uniqueProductCodes)

  return uniqueProductCodes
}

export async function AddDiscountedItemToCart(
  aoeCartIdToken: string,
  cartItemCreateCommon: ICartItemCreateCommon,
  lineNumberToDelete: number | undefined,
) {
  const logger = useStoreLogger()
  logger.debug(
    'AddDiscountedItemToCart',
    aoeCartIdToken,
    cartItemCreateCommon,
    lineNumberToDelete,
  )
  if (lineNumberToDelete) {
    const batchedRequests: ICartBatchedRequests = {
      aoeCartIdToken,
      batchedRequests: [
        { cartItemDelete: { lineNumber: lineNumberToDelete } },
        {
          cartItemCreate: cartItemCreateCommon,
        },
      ],
    }
    await CartBatchedRequests(batchedRequests).then(response => {
      logger.debug('cartItemCreate response', response)
      SaveCartToLocalStorage(response)
    })
  } else {
    const cartItemCreate: ICartItemCreate = {
      ...cartItemCreateCommon,
      aoeCartIdToken: aoeCartIdToken,
    }
    await CartItemCreate(cartItemCreate).then(response => {
      logger.debug('cartItemCreate response', response)
      SaveCartToLocalStorage(response)
    })
  }
  return null
}

export async function AddBatchedItemToCart(
  batchedRequests: ICartBatchedRequests,
) {
  const logger = useStoreLogger()
  logger.debug('AddBatchedItemToCart', batchedRequests)

  await await CartBatchedRequests(batchedRequests).then(response => {
    if (response) {
      return response
    }
  })
  return null
}

export function GetCurrentCartIdentifiers(): ICurrentCartIdentifiers | null {
  const { cookies } = useCookies()
  const aoeCartIdToken = cookies.get('AoeCartIdToken')

  if (aoeCartIdToken) {
    const index = aoeCartIdToken.indexOf('.')
    const aoeCartId = Number(aoeCartIdToken.slice(0, index))

    // Get the authenticated user's beeNumber from SessionInformation cookie
    // This is a synchronous function, so we can't use GetBeeNumberAuthenicatedUser
    // Instead, we'll try to get it from the cookie directly
    const sessionInfo = cookies.get('SessionInformation')
    let beeNumber: number | null = null

    if (sessionInfo) {
      try {
        // Try to extract beeNumber from the session info
        // This is a simplified approach - the actual implementation might differ
        const decodedToken = JSON.parse(atob(sessionInfo.split('.')[1]))
        if (decodedToken.BeeNumberAuthenicatedUser) {
          beeNumber = Number(decodedToken.BeeNumberAuthenicatedUser)
        }
      } catch (e) {
        console.error('Error parsing session info:', e)
      }
    }

    return {
      aoeCartId,
      aoeCartIdToken: aoeCartIdToken,
      beeNumber: beeNumber
    }
  }
  return null
}

export async function AddItemToCart(
  aoeCartIdToken: string | null,
  beeNumberPayee: number | null,
  beeNumberCustomer: number | null,
  orderTypeId: string,
  skuCode: string,
  quantity: number,
  poeDiscPlanLevelDiscId?: number,
  autoApplyKitCoupon?: boolean,
) {
  const logger = useStoreLogger()
  logger.debug('AddItemToCart', aoeCartIdToken, skuCode, quantity)

  if (aoeCartIdToken == null) {
    const cartIdentifiers = GetCurrentCartIdentifiers()
    if (cartIdentifiers && GetCartFromLocalStorage(cartIdentifiers.aoeCartIdToken)?.orderTypeId === orderTypeId) {
      logger.debug('cartIdentifiers=', cartIdentifiers)
      aoeCartIdToken = cartIdentifiers.aoeCartIdToken
    } else {
      logger.debug('cartIdentifiers=null. creating new cart')
      const cartCreateResponse = await CartCreate({
        orderTypeId: orderTypeId,
        beeNumberPayee: beeNumberPayee,
        beeNumberCustomer: beeNumberCustomer,
        autoApplyKitCoupon: autoApplyKitCoupon,
      })
      aoeCartIdToken = cartCreateResponse.aoeCartIdToken
      const { cookies } = useCookies()
      cookies.set('AoeCartIdToken', aoeCartIdToken)
    }
  }

  await CartItemCreate({
    aoeCartIdToken,
    skuCode,
    quantity,
    poeDiscPlanLevelDiscId,
  }).then(response => {
    logger.debug('cartItemCreate response', response)
    SaveCartToLocalStorage(response)
  })
}

export async function DeleteItemFromCart(cartItemDelete: ICartItemDelete) {
  await CartItemDelete(cartItemDelete).then(response => {
    SaveCartToLocalStorage(response)
  })
}

export async function UpdateItemInCart(cartItemUpdate: ICartItemUpdate) {
  await CartItemUpdate(cartItemUpdate).then(response => {
    SaveCartToLocalStorage(response)
  })
}

export async function ShippingAddressAddToCart(
  cartShippingAddressCreate: ICartShippingAddressCreate,
): Promise<IAddressVerification> {
  const addressVerification: IAddressVerification = {
    addressValidationMessageId: null,
    addressId: null,
    items: [],
    item: null
  }

  try {
    const response = await CartShippingAddressCreate(cartShippingAddressCreate)

    if (
      response.addressValidationMessageId != undefined &&
      response.addressValidationMessageId.length > 0
    ) {
      addressVerification.addressValidationMessageId =
        response.addressValidationMessageId

      if (
        response.validAddresses != undefined &&
        response.validAddresses.length > 0
      ) {
        for (const t of response.validAddresses) {
          addressVerification.items.push(t)
        }

        let count = 1
        for (const a of addressVerification.items) {
          a.addressId = count
          count++
        }
      }
    } else {
      addressVerification.item = response.item
      if (response.item != undefined)
      {
        SaveCartToLocalStorage(response)
      }
    }
  } catch (error) {
    // Re-throw the error so calling components can handle it with handleError
    throw error
  }

  return addressVerification
}

export async function BillingAddressAddToCart(
  cartBillingAddressCreate: ICartShippingAddressCreate,
): Promise<IAddressVerification> {
  const addressVerification: IAddressVerification = {
    addressValidationMessageId: null,
    addressId: null,
    items: [],
    item: null
  }
  await CartBillingAddressCreate(cartBillingAddressCreate).then(response => {
    if (
      response.addressValidationMessageId != undefined &&
      response.addressValidationMessageId.length > 0
    ) {
      addressVerification.addressValidationMessageId =
        response.addressValidationMessageId

      if (
        response.validAddresses != undefined &&
        response.validAddresses.length > 0
      ) {
        for (const t of response.validAddresses) {
          addressVerification.items.push(t)
        }

        let count = 1
        for (const a of addressVerification.items) {
          a.addressId = count
          count++
        }
      }
      return addressVerification
    } else {
      addressVerification.item = response.item
      if (response.item != undefined)
      {
        SaveCartToLocalStorage(response)
      }
    }
  })

  return addressVerification
}

export async function CustomerAddToCart(cartCustomerAdd: ICartCustomerAdd) {
  await CartCustomerAdd(cartCustomerAdd).then(response => {
    SaveCartToLocalStorage(response)
  })
}

export async function GuestAddToCart(cartGuestAdd: ICartGuestAdd) {
  await CartGuestAdd(cartGuestAdd).then(response => {
    SaveCartToLocalStorage(response)
  })
}

// Kit (starter kit) carts use their own order type and must never be linked to an event.
export async function IsKitCart(cart: IAoeCart | null | undefined): Promise<boolean> {
  if (!cart?.orderTypeId) return false
  const kitOrderTypeId = await GetKitOrderTypeId()
  return !!kitOrderTypeId && cart.orderTypeId === kitOrderTypeId
}

export async function EventAddToCart(cartEventAdd: ICartEventAdd) {
  // The current-cart cookie can point at a kit cart (AddItemToCart sets it when a kit is chosen), so
  // event links (?event= querystring, party locator popup) would otherwise attach the event to the
  // kit order, which breaks its export. The API refuses this too; skip the call here.
  const cart = await GetCartFromLocalStorageOrApi(cartEventAdd.aoeCartIdToken)
  if (await IsKitCart(cart)) {
    useStoreLogger().warn('EventAddToCart skipped - kit carts cannot be linked to an event', cartEventAdd)
    return
  }

  await CartEventAdd(cartEventAdd).then(response => {
    SaveCartToLocalStorage(response)
  })
}

export async function EventDeleteFromCart(cartEventDelete: ICartEventDelete) {
  await CartEventDelete(cartEventDelete).then(response => {
    SaveCartToLocalStorage(response)
  })
}

export async function PayeeAddToCart(cartPayeeAdd: ICartPayeeAdd) {
  await CartPayeeAdd(cartPayeeAdd).then(response => {
    SaveCartToLocalStorage(response)
  })
}
export function GetGamificationDifferenceMessage( t: ComposerTranslation, gamification: IAoeCartGamification): string
{
  const logger = useStoreLogger()
  logger.debug('GetGamificationDifferenceMessage gamification:', gamification)
  let messageKey = `gamificationDifferenceMessage.${gamification.requirements[0].systemKeyword}_${gamification.awards[0].systemKeyword}`;
  let messageText = t(messageKey,
    {totalAmount : FormatCurrency(gamification.requirements[0].totalAmount, null),
    difference : FormatCurrency(gamification.requirements[0].difference, null)})
    if (messageText === messageKey) {
      messageKey = `gamificationDifferenceMessage.${gamification.requirements[0].systemKeyword}`;
      messageText = t(messageKey,
        {totalAmount : FormatCurrency(gamification.requirements[0].totalAmount, null),
        difference : FormatCurrency(gamification.requirements[0].difference, null)})
    }
    return messageText;
}

export function GetFreeShippingGamificationDifferenceMessage( t: ComposerTranslation, cart: IAoeCart): string {
  const logger = useStoreLogger()
  logger.debug('GetFreeShippingGamificationDifferenceMessage', cart)
  if (cart && cart.gamification && cart.gamification.length > 0) {
    for (const gamification of cart.gamification) {
      if (gamification.awards) {
        const freeShippingAward = gamification.awards.find(
          awards => awards.systemKeyword === 'FreeShipping',
        )
        if (freeShippingAward) {
          if (gamification.requirements.length > 0) {
            const requirementSystemKeyword = gamification.requirements[0].systemKeyword + '_';
            const totalAmount = gamification.requirements[0].totalAmount;
            return t(`gamificationDifferenceMessage.${requirementSystemKeyword}FreeShipping`, {totalAmount : FormatCurrency(totalAmount, null)})
          }

        }
      }
    }
  }
  return ''
}


export async function DeletePaymentFromCart(
  cartPaymentDelete: ICartPaymentDelete,
) {
  await CartPaymentDelete(cartPaymentDelete).then(response => {
    SaveCartToLocalStorage(response)
  })
}

export async function ShipToTypeAddToCart(
  cartShipToTypeAdd: ICartShipToTypeCreate,
) {
  await CartShipToTypeCreate(cartShipToTypeAdd).then(response => {
    SaveCartToLocalStorage(response)
  })
}

export async function ShippingMethodAddToCart(
  cartShippingMethodAdd: ICartShippingMethodCreate,
) {
  await CartShippingMethodCreate(cartShippingMethodAdd).then(response => {
    SaveCartToLocalStorage(response)
  })
}

export async function FreeDollarCouponAddToCart(
  cartFDCAdd: ICartFreeDollarCouponAdd,
) {
  await CartFreeDollarCouponAdd(cartFDCAdd).then(response => {
    SaveCartToLocalStorage(response)
  })
}

export async function FreeDollarCouponRemoveFromCart(
  cartFDCAdd: ICartFreeDollarCouponAdd,
) {
  await CartFreeDollarCouponRemove(cartFDCAdd).then(response => {
    SaveCartToLocalStorage(response)
  })
}

export async function UpdateCartActive(cartActiveUpdate: ICartActiveUpdate) {
  await CartActiveUpdate(cartActiveUpdate).then(response => {
    SaveCartToLocalStorage(response)
  })
}

export async function CreateConsultantLeadCart(
  cartConsLead: ICartTokenRequest,
) {
  await CartConsultantLeadCreate(cartConsLead).then(response => {
    SaveCartToLocalStorage(response)
  })
}

export async function DeleteConsultantLeadCart(
  cartConsLead: ICartTokenRequest,
) {
  await CartConsultantLeadDelete(cartConsLead).then(response => {
    SaveCartToLocalStorage(response)
  })
}

export async function CreatePartyLeadCart(cartPartyLead: ICartPartyLead) {
  await CartPartyLeadCreate(cartPartyLead).then(response => {
    SaveCartToLocalStorage(response)
  })
}

export async function DeletePartyLeadCart(cartPartyLead: ICartPartyLead) {
  await CartPartyLeadDelete(cartPartyLead).then(response => {
    SaveCartToLocalStorage(response)
  })
}

export async function CreateInvoiceCart(cartInvoice: ICartTokenRequest) {
  await CartInvoiceCreate(cartInvoice).then(response => {
    SaveCartToLocalStorage(response)
  })
}

export async function UpdateInvoiceCart(cartInvoice: ICartTokenRequest) {
  return await CartInvoiceUpdate(cartInvoice)
}

export async function CancelInvoiceCart(cartInvoice: ICartTokenRequest) {
  return await CartInvoiceCancel(cartInvoice)
}

export async function ResendInvoiceCart(cartInvoice: ICartTokenRequest) {
  return await CartInvoiceResend(cartInvoice)
}

export async function GetInvoice(
  invoice: IAoeInvoiceRequest,
): Promise<IAoeInvoiceResponse | null> {
  const invoiceResponse = await InvoiceGet(invoice)
  return invoiceResponse
}

export async function GetInvoiceList(): Promise<IAoeInvoiceListResponse> {
  const invoiceResponse = await InvoiceGetList()
  return invoiceResponse
}

export async function SubmitInvoice(
  invoice: IAoeInvoiceSubmitRequest,
): Promise<IAoeInvoiceSubmitResponse | null> {
  const invoiceResponse = await InvoiceSubmit(invoice)
  return invoiceResponse
}

export async function CheckInvoiceExists(
  invoice: IAoeInvoiceRequest,
) {
  return await InvoiceExistsApi(invoice)
}

export async function VerifyInvoiceIdentity(
  request: IAoeInvoiceVerifyIdentityRequest,
): Promise<IAoeInvoiceResponse | null> {
  return await InvoiceVerifyIdentityApi(request)
}

export async function ApplyCouponToCart(cartCouponCreate: ICartCouponCreate) {
  const response = await CartCouponCreate(cartCouponCreate)
  SaveCartToLocalStorage(response)
  return response
}

export function EarnedDiscountsToDescriptionList(
  t: ComposerTranslation,
  earnedDiscounts: IAoeCartEarnedDiscount[] | undefined | null,
): string[] | undefined {
  if (earnedDiscounts) {
    const descriptions: string[] = []
    for (const earnedDiscount of earnedDiscounts) {
      if (earnedDiscount.systemKeyword) {
        let messagekey =
          'earnedDiscountToDescription.' + earnedDiscount.systemKeyword
        let description = t(messagekey, earnedDiscount)
        if (description === messagekey) {
          messagekey = 'earnedDiscountToDescription.Default'
          description = t(messagekey, earnedDiscount)
        }
        descriptions.push(description)
      }
      else {
        const messagekey = 'earnedDiscountToDescription.Default'
        const description = t(messagekey, earnedDiscount)
        descriptions.push(description)
      }
    }
    return descriptions
  } else {
    return undefined
  }
}


export async function GetCartByCustomer(cartGetByCustomer: ICartGetByCustomerRequest): Promise<ICartResponse | null> {
  try
  {
    const cartResponse = await CartGetByCustomer(cartGetByCustomer)
    if (cartResponse)
    {
      return cartResponse
    }
  }
  catch (error)
  {
    logger.debug("Error in CartGetByCustomer: ", error)
    //need to make new cart
    const cartCreateResponse = await CartCreate({
      orderTypeId: cartGetByCustomer.orderTypeId,
      beeNumberPayee: cartGetByCustomer.beeNumberPayee ?? cartGetByCustomer.beeNumberCustomer,
      beeNumberCustomer: cartGetByCustomer.beeNumberCustomer,
    })
    if (cartCreateResponse)
    {
      const newCartResponse = await CartGetByCustomer(cartGetByCustomer)
      return newCartResponse
    }
  }
  return null
}
